import { ref, computed, type Ref } from 'vue';
import { queryClient } from '@/shared/queryClient';
import { botsQueryOptions } from '@/composables/useBots';
import { teamMembersQueryOptions } from '@/composables/useTeamMembers';
import { businessRolesQueryOptions } from '@/composables/useBusinessRoles';
import { complianceKeywordsQueryOptions } from '@/composables/useComplianceKeywords';

export interface PopperDataOptions {
  channel: Ref<string | undefined>
  isAgentGroupReq: Ref<boolean>
}

export function usePopperData({ channel, isAgentGroupReq }: PopperDataOptions) {
  const allBots = ref<any[]>([]);
  const allRoles = ref<any[]>([]);
  const allAgents = ref<any[]>([]);
  const complianceData = ref<any[]>([]);
  const isLoading = ref(false);

  // A compliance rule counts as "configured" only when it has an actual message
  // or template set up. `message` is an object (empty `{}` when unconfigured), and
  // WhatsApp template rules keep their content in `template_data` while `message`
  // stays empty — so checking `!!item.message` alone both falsely enables empty
  // rules (`{}` is truthy) and falsely disables template-only rules.
  const hasContent = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.some(hasContent);
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return !!value;
  };

  const isConfigured = (item: any): boolean => {
    const message = item?.message;

    // The message-type discriminator differs by source: the API GET response uses
    // `message.type` while the local save shape uses `message.template_type`. This
    // mirrors handleRuleSelected() in whatsapp-compliance-keywords.vue, which is the
    // source of truth for how a saved rule is interpreted.
    const msgType: string | undefined = message?.type || message?.template_type;

    // Text rule (normalchat): configured when the human-facing message has content.
    // `message.message` may be a string (API GET) or an array (local save) — hasContent handles both.
    if (msgType === 'normalchat' && hasContent(message?.message)) return true;

    // Quick-reply rule: configured when it has reply buttons. The API may nest them
    // at message.template.buttons or expose them at message.buttons.
    if (msgType?.startsWith('quickreplies') &&
        (hasContent(message?.template?.buttons) || hasContent(message?.buttons))) {
      return true;
    }

    // Fallbacks that are independent of the type discriminator, so rules coming from
    // other flows (e.g. bot builder) keep working:
    // - any non-empty message text
    if (hasContent(message?.message)) return true;
    // - template/button content carried directly on the message object
    if (hasContent(message?.template) || hasContent(message?.buttons)) return true;
    // WhatsApp template rules keep their content in template_data.
    if (hasContent(item?.template_data)) return true;

    return false;
  };

  const optInConfigured = computed(() =>
    Array.isArray(complianceData.value) &&
    complianceData.value.some(item => item.active && item.type === 'opt_in' && isConfigured(item))
  );

  const optOutConfigured = computed(() =>
    Array.isArray(complianceData.value) &&
    complianceData.value.some(item => item.active && item.type === 'opt_out' && isConfigured(item))
  );

  const hasOptIn = computed(() =>
    Array.isArray(complianceData.value) && complianceData.value.some(item => item.active && item.type === 'opt_in')
  );

  const hasOptOut = computed(() =>
    Array.isArray(complianceData.value) && complianceData.value.some(item => item.active && item.type === 'opt_out')
  );

  async function fetchAll() {
    isLoading.value = true;
    try {
      const botsPromise = queryClient.ensureQueryData(
        botsQueryOptions({ isActive: true, channel: channel.value || null })
      );

      let rolesPromise: Promise<any> | undefined;
      let agentsPromise: Promise<any> | undefined;

      if (isAgentGroupReq.value) {
        rolesPromise = queryClient.ensureQueryData(businessRolesQueryOptions);
        agentsPromise = queryClient.ensureQueryData(teamMembersQueryOptions);
      }

      const compliancePromise = channel.value
        ? queryClient.fetchQuery({ ...complianceKeywordsQueryOptions, staleTime: 0 }).catch(() => [])
        : Promise.resolve([]);

      const [botsData, rolesData, agentsData, allComplianceData] = await Promise.all([
        botsPromise,
        rolesPromise,
        agentsPromise,
        compliancePromise,
      ]);

      allBots.value = Array.isArray(botsData)
        ? botsData.filter((e: any) => e && e.active && e.id && e.displayname)
        : [];

      if (isAgentGroupReq.value) {
        allRoles.value = rolesData || [];
        allAgents.value = agentsData || [];
      }

      complianceData.value = Array.isArray(allComplianceData) && channel.value
        ? allComplianceData.filter((item: any) => item.channel === channel.value)
        : [];
    } finally {
      isLoading.value = false;
    }
  }

  return {
    allBots,
    allRoles,
    allAgents,
    complianceData,
    isLoading,
    optInConfigured,
    optOutConfigured,
    hasOptIn,
    hasOptOut,
    fetchAll,
  };
}
